import React, { useEffect, useMemo, useState } from 'react';
import { Segmented, Select, Typography } from 'antd';

import RecordBadge from '../common/RecordBadge';
import FilterBuilder from '../common/FilterBuilder';
import FilterChips from '../common/FilterChips';
import { CardOptionLabel, buildMemberCardOptions } from '../cards/CardVisuals';
import { api } from '../../api/client';
import { API_COLLECTIONS_URL } from '../../utils';
import { buildFilterFields } from '../../utils/kqlBuilder';
import { getCollectionFacetCardKeys, getCollectionMemberCardIds } from '../../utils/collections';
import { normalizeId } from '../../utils/ids';
import { linkShape } from '../../services/collectionLinks';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import useAccessTagsField from '../../hooks/useAccessTagsField';

const { Text } = Typography;

/**
 * THE SHAPE OF A LINK — what part of a collection it covers — read and chosen
 * with the same parts a grant's scope is (SharingTab): the card picker in
 * hierarchy order with the card's own badge on every row, and the shared
 * filter builder over the collection's vocabulary. A link is one shape per
 * row: the whole collection, one card (and its child cards), or a filter —
 * never a card and a filter together.
 */

/**
 * Match on the option title; NEVER match group headers (rc-select passes them
 * through filterOption too — a header match would dump every child into the
 * results). Shared by every card-scope picker on the Sharing tab.
 */
export const filterScopeOption = (input, option) => {
	if (Array.isArray(option?.options)) return false;
	return (option?.title || '').toLowerCase().includes((input || '').toLowerCase());
};

export const CARD_SHAPE_HINT = 'A card link covers the card and its child cards.';
export const LINK_ME_NOTE = 'A link\'s filter cannot use me: the people reading through a link are the other collection\'s members, not you.';

export const SHAPE_OPTIONS = [
	{ value: 'whole', label: 'Whole collection' },
	{ value: 'card', label: 'One card' },
	{ value: 'query', label: 'Filtered records' },
];

/** A shape draft is complete when it names what its kind needs. */
export function shapeComplete(shape) {
	if (!shape) return false;
	if (shape.kind === 'card') return Boolean(normalizeId(shape.cardId));
	if (shape.kind === 'query') return Boolean((shape.query || '').trim());
	return shape.kind === 'whole';
}

/** A draft as the service's shape argument: `{ card_id }`, `{ query }` or `{}`. */
export function shapeToPayload(shape) {
	if (shape?.kind === 'card') return { card_id: normalizeId(shape.cardId) };
	if (shape?.kind === 'query') return { query: (shape.query || '').trim() };
	return {};
}

/**
 * The picker's vocabulary for one collection: its member cards as options
 * (hierarchy order, branded, the embedded ref's name as a fallback for a
 * card the cache does not hold) and its filter fields — the same two lists
 * the Sharing tab builds for a grant.
 */
export function buildLinkVocabulary({ collection, cardsById = {}, getElementsForCard } = {}) {
	if (!collection) return { cardOptions: [], cardMetaById: new Map(), filterFields: [] };
	const defs = Object.values(cardsById || {});
	const memberCardIds = getCollectionMemberCardIds(collection, defs);
	const refs = Array.isArray(collection?.cards) ? collection.cards : [];
	const nameByRefId = new Map(refs
		.map(ref => [normalizeId(ref?.cardDefinitionId), ref?.name])
		.filter(([id]) => id));
	const fromDefs = buildMemberCardOptions(defs, memberCardIds);
	const covered = new Set(fromDefs.map(opt => opt.value));
	const refOnly = [...memberCardIds]
		.filter(id => !covered.has(id))
		.map(id => ({ value: id, card: { name: nameByRefId.get(id) || 'Untitled card' }, depth: 0 }));
	const cardOptions = [...fromDefs, ...refOnly];
	return {
		cardOptions,
		cardMetaById: new Map(cardOptions.map(opt => [opt.value, opt.card])),
		// A link's filter is who-sees-what text: the API resolves its keys
		// strictly, so the builder keeps them (buildFilterFields `keepKeys`).
		filterFields: buildFilterFields({
			cards: defs,
			cardIds: memberCardIds.size ? memberCardIds : null,
			getElementsForCard,
			facetCardKeys: getCollectionFacetCardKeys(collection),
			keepKeys: true,
			noGroups: true,
		}),
	};
}

/**
 * The vocabulary of a collection other than the one on screen — the source
 * of a link being requested. Its document is fetched once per id (the caller
 * reads it of their own; that is what lets them ask) and its cards come from
 * the definitions cache. A link's filter may name that collection's
 * RESTRICTED tags (T7, access mode): its `tags` field rides along, holding
 * what its tag list tells this reader (hooks/useAccessTagsField).
 */
export function useLinkVocabulary(collectionId) {
	const defs = useOptionalCardDefinitions();
	const cardsById = defs?.cardsById;
	const getElementsForCard = defs?.getElementsForCard;
	const [collection, setCollection] = useState(null);
	const id = normalizeId(collectionId);

	useEffect(() => {
		setCollection(null);
		if (!id) return undefined;
		let active = true;
		api.get(`${API_COLLECTIONS_URL}/${id}`)
			.then(({ data }) => { if (active) setCollection(data || null); })
			.catch(() => { if (active) setCollection(null); });
		return () => { active = false; };
	}, [id]);

	const tagsField = useAccessTagsField(id, { enabled: Boolean(collection) });
	return useMemo(() => {
		const vocabulary = buildLinkVocabulary({ collection, cardsById, getElementsForCard });
		return tagsField && collection ? { ...vocabulary, filterFields: [...vocabulary.filterFields, tagsField] } : vocabulary;
	}, [collection, cardsById, getElementsForCard, tagsField]);
}

/**
 * A link's (or a request's) shape, read: "Whole collection", the card's
 * badge, or the filter as chips. `cardMeta(id)` names a card; a card the
 * caller cannot name still gets its badge, as "A card".
 */
export function LinkShapeSummary({ row, cardMeta = () => null, filterFields = [], className = '' }) {
	const shape = linkShape(row);
	const classes = ['link-shape', className].filter(Boolean).join(' ');
	if (shape.kind === 'card') {
		const meta = cardMeta(shape.cardId);
		return (
			<span className={classes} title={CARD_SHAPE_HINT}>
				<RecordBadge kind="card" record={meta || null} title={meta?.name || 'A card'} size="xs" />
			</span>
		);
	}
	if (shape.kind === 'query') {
		return (
			<span className={`${classes} link-shape--query`}>
				<Text type="secondary" className="link-shape__label">Records matching</Text>
				<FilterChips readOnly query={shape.query} fields={filterFields} className="link-shape__chips" />
			</span>
		);
	}
	if (shape.kind === 'removed') return <Text type="secondary" className={classes}>What it shared is no longer recorded</Text>;
	return <Text type="secondary" className={classes}>Whole collection</Text>;
}

/**
 * Choosing a shape. `kinds` limits the choice (an approval may only narrow);
 * `cardIds`, when given, limits the cards offered (a card request narrows to
 * that card or a descendant). The filter builder refuses `me` with the
 * link's own reason.
 */
export function LinkShapePicker({
	value,
	onChange,
	cardOptions = [],
	filterFields = [],
	collectionId = null,
	kinds = ['whole', 'card', 'query'],
	cardIds = null,
	disabled = false,
}) {
	const shape = value || { kind: 'whole', cardId: null, query: null };
	const allowed = cardIds ? new Set([...cardIds].map(normalizeId)) : null;
	const cardSelectOptions = cardOptions
		.filter(opt => !allowed || allowed.has(opt.value))
		.map(opt => ({
			value: opt.value,
			title: opt.card?.name || 'Untitled card',
			label: opt.card?.name || 'Untitled card',
			card: opt.card,
			depth: opt.depth,
		}));
	const options = SHAPE_OPTIONS.filter(opt => kinds.includes(opt.value));

	return (
		<div className="link-shape-picker">
			{options.length > 1 ? (
				<Segmented
					aria-label="What to share"
					value={shape.kind}
					disabled={disabled}
					onChange={kind => onChange({ kind, cardId: null, query: null })}
					options={options}
				/>
			) : null}
			{shape.kind === 'card' ? (
				<div className="link-shape-picker__detail">
					<Select
						className="link-shape-picker__card"
						aria-label="Card"
						showSearch
						disabled={disabled}
						value={shape.cardId || undefined}
						onChange={cardId => onChange({ kind: 'card', cardId, query: null })}
						options={cardSelectOptions}
						optionRender={option => <CardOptionLabel card={option.data.card} depth={option.data.depth} />}
						filterOption={filterScopeOption}
						placeholder={cardSelectOptions.length ? 'Pick a card' : 'No cards to pick from'}
						notFoundContent="No cards match"
					/>
					<Text type="secondary" className="small">{CARD_SHAPE_HINT}</Text>
				</div>
			) : null}
			{shape.kind === 'query' ? (
				<div className="link-shape-picker__detail">
					<FilterBuilder
						collectionId={collectionId}
						scopeKey={collectionId ? `collection-link:${collectionId}` : null}
						fields={filterFields}
						query={shape.query || ''}
						onApply={text => onChange({ kind: 'query', cardId: null, query: text || null })}
						emptyText="Add the conditions a record must meet to be shared."
						disallowMe
						meDisallowedNote={LINK_ME_NOTE}
						// What a link shares is read in every sub-collection:
						// the server resolves its names, and asks (⚑ K4).
						keepElementNames
					/>
					{(shape.query || '').trim() ? (
						<FilterChips readOnly query={shape.query} fields={filterFields} className="link-shape__chips" />
					) : (
						<Text type="secondary" className="small">Only the records the filter matches are shared, read-only.</Text>
					)}
				</div>
			) : null}
		</div>
	);
}
